// owner/screens/Booking.js — one booking (/bookings/:id), read-only, the approved mock-up
// (owner-app-mockups/booking-phone.html) with Adil's 4 Nov changes:
//   - the stay card shows dates, years and weekdays, no times (the only times on record are the unit's defaults);
//   - money: total, the booking site's fee, net — today's rules: the fee only when > 0, the net only when > 0 and
//     different from the total; no fee percentage, no nightly rate, no «قبل الضريبة» split; a «شامل الضريبة» /
//     «غير شامل الضريبة» tag read from the booking's own stamp (`vatFooting.value`), nothing when unknown;
//   - never Host Me's own fee; no guest contact or guest notes (the server does not send them to owners).
// Also the alert tap's landing page: the booking is always re-read through the server's access check.

import React, { useMemo } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import useLanguage from '../../hooks/useLanguage';
import { joinList } from '../../utils/localeText';
import Icon from '../ui/Icon';
import PlatformMark, { usePlatformName } from '../ui/PlatformMark';
import { BookingStatusChip } from '../ui/BookingRow';
import { UnitThumb } from '../ui/UnitBits';
import { SkeletonBlocks, ErrorState, EmptyState } from '../ui/States';
import useOwnerFormat from '../ui/useOwnerFormat';
import { useOwnerBooking, useOwnerUnits } from '../api/ownerReads';

const VAT_TAGS = { inclusive: 'vatInclusive', exclusive: 'vatExclusive' };

const StayCard = ({ booking }) => {
  const { t, language } = useLanguage();
  const f = useOwnerFormat();
  const day = (label, value) => (
    <div className="o-stay-d">
      <p className="o-lbl">{label}</p>
      <p className="o-dt">{f.dayMonth(value)}</p>
      <p className="o-wd">{joinList([f.weekday(value), f.year(value)], language)}</p>
    </div>
  );
  return (
    <section className="o-section o-section-tight">
      <div className="o-sheet o-stay">
        {day(t('ownerApp.booking.checkIn'), booking.checkIn)}
        <div className="o-stay-n" aria-hidden={!Number.isInteger(booking.nights)}>
          <Icon name="moon" size="sm" />
          <span className="o-stay-line" />
          {Number.isInteger(booking.nights) && <span>{f.nights(booking.nights)}</span>}
        </div>
        {day(t('ownerApp.common.checkOut'), booking.checkOut)}
      </div>
    </section>
  );
};

const Money = ({ booking }) => {
  const { t } = useLanguage();
  const f = useOwnerFormat();
  const nameOf = usePlatformName();
  const total = Number(booking.totalAmount);
  const fee = Number(booking.commission);
  const net = Number(booking.netAmount);
  const vatTag = VAT_TAGS[booking.vatFooting?.value];
  const amount = (value, sign = '') => (
    <span className="o-v"><span className="o-money">{`${sign}${f.amount(value)}`}</span> <span className="o-cur">{f.currency}</span></span>
  );

  return (
    <section className="o-section o-section-last">
      <div className="o-section-head"><h2>{t('ownerApp.booking.money')}</h2></div>
      <div className="o-sheet">
        {total > 0 ? (
          <ul className={`o-moneyblock${booking.status === 'cancelled' ? ' cancelled' : ''}`}>
            <li>
              <span className="o-k">
                {t('ownerApp.booking.total')}
                {vatTag && <span className="o-vat-tag">{t(`ownerApp.booking.${vatTag}`)}</span>}
              </span>
              {amount(total)}
            </li>
            {fee > 0 && (
              <li className="o-fee">
                <span className="o-k">{t('ownerApp.booking.siteFee')}<small>{nameOf(booking.sourcePlatform)}</small></span>
                {amount(fee, '−')}
              </li>
            )}
            {net > 0 && net !== total && (
              <li className="o-net"><span className="o-k">{t('ownerApp.common.netAfterSiteFee')}</span>{amount(net)}</li>
            )}
          </ul>
        ) : (
          <p className="o-empty-row">{t('ownerApp.booking.noMoneyYet')}</p>
        )}
      </div>
    </section>
  );
};

const Booking = () => {
  const { id } = useParams();
  const { t } = useLanguage();
  const f = useOwnerFormat();
  const location = useLocation();
  const navigate = useNavigate();
  const booking = useOwnerBooking(id);
  const units = useOwnerUnits();

  const photo = useMemo(() => {
    const unitId = String(booking.data?.property?._id || '');
    const unit = (Array.isArray(units.data) ? units.data : []).find((u) => String(u._id) === unitId);
    return unit?.bookingPageSettings?.primaryPhoto;
  }, [booking.data, units.data]);

  // Back to wherever the owner came from (the list, a unit's tab, the overview); an alert tap
  // opens the page with no history behind it, so it goes to the bookings list.
  const back = location.key !== 'default'
    ? <button type="button" className="o-back" onClick={() => navigate(-1)} aria-label={t('ownerApp.common.back')}><Icon name="back" /></button>
    : <Link to="/bookings" className="o-back" aria-label={t('ownerApp.common.back')}><Icon name="back" /></Link>;

  if (booking.isLoading) return <SkeletonBlocks blocks={3} />;
  if (booking.isError) {
    const status = booking.error?.response?.status;
    if (status === 400 || status === 403 || status === 404) {
      return (
        <EmptyState icon="calendar" title={t('ownerApp.booking.notYours')}
          action={<Link className="o-btn o-btn-primary" to="/bookings">{t('ownerApp.booking.backToBookings')}</Link>} />
      );
    }
    return <ErrorState title={t('ownerApp.booking.loadError')} onRetry={booking.refetch} />;
  }

  const b = booking.data;
  const unit = b.property || {};
  return (
    <>
      {back}
      <div className="o-detail-hero">
        <UnitThumb photo={photo} large />
        <div className="o-body">
          <h1>{unit.name}</h1>
          {unit.propertyCode && <span className="o-code o-ltr">{unit.propertyCode}</span>}
        </div>
        <BookingStatusChip status={b.status} />
      </div>

      <StayCard booking={b} />

      <section className="o-section o-section-tight">
        <ul className="o-sheet o-kv">
          <li><span className="o-k"><Icon name="guest" size="sm" />{t('ownerApp.booking.guest')}</span>
            <span className="o-v">{b.guestName || t('ownerApp.unit.guestFallback')}</span></li>
          {Number(b.numberOfGuests) > 0 && (
            <li><span className="o-k"><Icon name="users" size="sm" />{t('ownerApp.booking.guestCount')}</span>
              <span className="o-v">{f.guests(b.numberOfGuests)}</span></li>
          )}
          <li><span className="o-k"><Icon name="globe" size="sm" />{t('ownerApp.booking.site')}</span>
            <span className="o-v"><PlatformMark platform={b.sourcePlatform} withName /></span></li>
          {b.confirmationCode && (
            <li><span className="o-k"><Icon name="hash" size="sm" />{t('ownerApp.booking.code')}</span>
              <span className="o-v o-ltr">{b.confirmationCode}</span></li>
          )}
        </ul>
      </section>

      <Money booking={b} />
    </>
  );
};

export default Booking;
